/**
 * SupportTab — Tickets list, detail drawer with threaded messages, status changes.
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, MessageSquare, Send, LifeBuoy, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { superAdminApi } from './superAdminApi';

const STATUS_COLORS = {
  open: 'bg-sky-500/20 text-sky-300 border-sky-400/30',
  in_progress: 'bg-amber-500/20 text-amber-300 border-amber-400/30',
  waiting_tenant: 'bg-fuchsia-500/20 text-fuchsia-300 border-fuchsia-400/30',
  resolved: 'bg-emerald-500/20 text-emerald-300 border-emerald-400/30',
  closed: 'bg-slate-500/20 text-slate-400 border-slate-400/30',
};

const PRIORITY_COLORS = {
  urgent: 'bg-rose-500/20 text-rose-300 border-rose-400/30',
  high: 'bg-orange-500/20 text-orange-300 border-orange-400/30',
  normal: 'bg-slate-500/20 text-slate-300 border-slate-400/30',
  low: 'bg-slate-600/20 text-slate-400 border-slate-500/30',
};

export const SupportTab = () => {
  const [statusFilter, setStatusFilter] = useState('all');
  const [openId, setOpenId] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const qc = useQueryClient();

  const params = {};
  if (statusFilter !== 'all') params.status = statusFilter;

  const { data: tickets = [], isLoading } = useQuery({
    queryKey: ['superadmin-tickets', statusFilter],
    queryFn: () => superAdminApi.listTickets(params),
  });

  const { data: stats } = useQuery({
    queryKey: ['superadmin-tickets-stats'],
    queryFn: superAdminApi.ticketsStats,
    refetchInterval: 30000,
  });

  const { data: tenants = [] } = useQuery({
    queryKey: ['superadmin-tenants', {}],
    queryFn: () => superAdminApi.listTenants({ limit: 500 }),
  });

  return (
    <div className="space-y-4" data-testid="superadmin-support-tab">
      {/* Stats strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI label="Total" value={stats?.total ?? 0} icon={LifeBuoy} color="slate" />
        <KPI label="Open" value={stats?.open ?? 0} icon={MessageSquare} color="sky" />
        <KPI label="Urgent" value={stats?.urgent_open ?? 0} icon={AlertCircle} color="rose" />
        <KPI label="Resolved" value={(stats?.by_status || {}).resolved ?? 0} icon={LifeBuoy} color="emerald" />
      </div>

      {/* Filters + actions */}
      <div className="flex flex-wrap items-center gap-2">
        {['all', 'open', 'in_progress', 'waiting_tenant', 'resolved', 'closed'].map(s => (
          <Button key={s} size="sm" variant={statusFilter === s ? 'default' : 'outline'}
                  onClick={() => setStatusFilter(s)}
                  className={statusFilter === s ? 'bg-indigo-500 hover:bg-indigo-400 text-white' : 'text-white border-white/20 bg-white/5 hover:bg-white/10'}
                  data-testid={`support-filter-${s}`}>
            <span className="capitalize">{s.replace('_', ' ')}</span>
          </Button>
        ))}
        <Button onClick={() => setCreateOpen(true)} size="sm"
                className="ml-auto bg-indigo-500 hover:bg-indigo-400 text-white" data-testid="create-ticket-btn">
          <Plus className="h-4 w-4 mr-1" /> New Ticket
        </Button>
      </div>

      <Card className="bg-white/5 backdrop-blur-xl border-white/10 text-white">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-white/10 text-xs uppercase tracking-wider text-slate-400">
                <tr>
                  <th className="text-left p-3">Ticket #</th>
                  <th className="text-left p-3">Tenant</th>
                  <th className="text-left p-3">Subject</th>
                  <th className="text-left p-3">Priority</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-right p-3">Messages</th>
                  <th className="text-left p-3">Created</th>
                </tr>
              </thead>
              <tbody>
                {isLoading && <tr><td colSpan={7} className="p-6 text-center text-slate-500">Loading...</td></tr>}
                {!isLoading && tickets.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-slate-500">No tickets.</td></tr>}
                {tickets.map(t => (
                  <tr key={t.id} onClick={() => setOpenId(t.id)}
                      className="border-b border-white/5 hover:bg-white/5 cursor-pointer"
                      data-testid={`support-row-${t.id}`}>
                    <td className="p-3 font-mono text-xs">{t.number}</td>
                    <td className="p-3">
                      <p className="font-semibold">{t.tenant_name || '—'}</p>
                      <p className="text-xs text-slate-500">{t.tenant_code}</p>
                    </td>
                    <td className="p-3">{t.subject}</td>
                    <td className="p-3">
                      <Badge variant="outline" className={`capitalize ${PRIORITY_COLORS[t.priority] || PRIORITY_COLORS.normal}`}>{t.priority}</Badge>
                    </td>
                    <td className="p-3">
                      <Badge variant="outline" className={`capitalize ${STATUS_COLORS[t.status] || ''}`}>{t.status?.replace('_', ' ')}</Badge>
                    </td>
                    <td className="p-3 text-right font-mono text-slate-300">{t.message_count ?? 0}</td>
                    <td className="p-3 text-xs text-slate-400">
                      {t.created_at ? new Date(t.created_at).toLocaleString() : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <CreateTicketDialog open={createOpen} onOpenChange={setCreateOpen}
                          tenants={tenants}
                          onCreated={() => {
                            qc.invalidateQueries({ queryKey: ['superadmin-tickets'] });
                            qc.invalidateQueries({ queryKey: ['superadmin-tickets-stats'] });
                          }} />
      {openId && <TicketDetailDialog ticketId={openId} onClose={() => setOpenId(null)} />}
    </div>
  );
};

const KPI = ({ label, value, icon: Icon, color }) => {
  const bg = {
    slate: 'bg-slate-500/20 border-slate-400/30', sky: 'bg-sky-500/20 border-sky-400/30',
    rose: 'bg-rose-500/20 border-rose-400/30', emerald: 'bg-emerald-500/20 border-emerald-400/30',
  }[color] || 'bg-slate-500/20 border-slate-400/30';
  const text = { slate: 'text-slate-300', sky: 'text-sky-300', rose: 'text-rose-300', emerald: 'text-emerald-300' }[color];
  return (
    <Card className="bg-white/5 border-white/10 text-white">
      <CardContent className="pt-4 pb-3 flex items-center justify-between">
        <div>
          <p className="text-xs text-slate-500 uppercase tracking-wider">{label}</p>
          <p className="text-2xl font-bold">{value}</p>
        </div>
        <div className={`w-9 h-9 rounded-lg border flex items-center justify-center ${bg}`}>
          <Icon className={`h-4 w-4 ${text}`} />
        </div>
      </CardContent>
    </Card>
  );
};

const CreateTicketDialog = ({ open, onOpenChange, tenants, onCreated }) => {
  const [form, setForm] = useState({
    tenant_id: '', subject: '', body: '', category: 'general', priority: 'normal',
  });
  const mut = useMutation({
    mutationFn: (data) => superAdminApi.createTicket(data),
    onSuccess: () => { toast.success('Ticket created'); onOpenChange(false); onCreated?.(); setForm({ tenant_id: '', subject: '', body: '', category: 'general', priority: 'normal' }); },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Failed'),
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 text-white border-white/10">
        <DialogHeader>
          <DialogTitle>New Support Ticket</DialogTitle>
          <DialogDescription className="text-slate-400">Open a ticket on behalf of a tenant</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label>Tenant *</Label>
            <Select value={form.tenant_id} onValueChange={v => setForm({ ...form, tenant_id: v })}>
              <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="ticket-tenant-select"><SelectValue placeholder="Select tenant" /></SelectTrigger>
              <SelectContent>
                {tenants.map(t => <SelectItem key={t.tenant_id || t.id} value={t.tenant_id || t.id}>{t.name} ({t.code})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Subject *</Label>
            <Input value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })}
                   className="bg-white/5 border-white/10 text-white" data-testid="ticket-subject" />
          </div>
          <div><Label>Body *</Label>
            <textarea value={form.body} onChange={e => setForm({ ...form, body: e.target.value })}
                      className="w-full bg-white/5 border border-white/10 rounded-lg p-2 text-sm text-white min-h-[100px]"
                      data-testid="ticket-body" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Category</Label>
              <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['general', 'billing', 'technical', 'feature_request', 'bug'].map(c =>
                    <SelectItem key={c} value={c} className="capitalize">{c.replace('_', ' ')}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Priority</Label>
              <Select value={form.priority} onValueChange={v => setForm({ ...form, priority: v })}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['low', 'normal', 'high', 'urgent'].map(p =>
                    <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="text-white border-white/20 bg-white/5">Cancel</Button>
          <Button onClick={() => mut.mutate(form)}
                  disabled={mut.isPending || !form.tenant_id || !form.subject || !form.body}
                  className="bg-indigo-500 hover:bg-indigo-400 text-white" data-testid="ticket-submit-btn">
            {mut.isPending ? 'Creating...' : 'Create Ticket'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const TicketDetailDialog = ({ ticketId, onClose }) => {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['ticket-detail', ticketId],
    queryFn: () => superAdminApi.ticketDetail(ticketId),
    enabled: !!ticketId,
  });
  const [reply, setReply] = useState('');
  const respondMut = useMutation({
    mutationFn: () => superAdminApi.respondTicket(ticketId, reply),
    onSuccess: () => { toast.success('Reply sent'); setReply(''); qc.invalidateQueries({ queryKey: ['ticket-detail', ticketId] }); qc.invalidateQueries({ queryKey: ['superadmin-tickets'] }); },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Failed'),
  });
  const statusMut = useMutation({
    mutationFn: (status) => superAdminApi.changeTicketStatus(ticketId, status),
    onSuccess: (_, status) => { toast.success(`Marked ${status}`); qc.invalidateQueries({ queryKey: ['ticket-detail', ticketId] }); qc.invalidateQueries({ queryKey: ['superadmin-tickets'] }); qc.invalidateQueries({ queryKey: ['superadmin-tickets-stats'] }); },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Failed'),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-slate-900 text-white border-white/10 max-w-3xl max-h-[90vh] overflow-y-auto">
        {isLoading ? <p className="p-6 text-slate-500">Loading...</p> : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <span className="font-mono text-xs text-slate-500">{data?.number}</span>
                <span>{data?.subject}</span>
              </DialogTitle>
              <DialogDescription className="text-slate-400">
                {data?.tenant_name} · {data?.category} · <Badge variant="outline" className={`capitalize ${PRIORITY_COLORS[data?.priority] || ''}`}>{data?.priority}</Badge>
              </DialogDescription>
            </DialogHeader>

            {/* Status actions */}
            <div className="flex gap-2 flex-wrap">
              {['open', 'in_progress', 'waiting_tenant', 'resolved', 'closed'].map(s => (
                <Button key={s} size="sm" variant="outline"
                        onClick={() => statusMut.mutate(s)}
                        disabled={data?.status === s}
                        className={data?.status === s
                          ? `border-white/30 ${STATUS_COLORS[s]}`
                          : 'text-white border-white/20 bg-white/5 hover:bg-white/10'}
                        data-testid={`ticket-status-${s}`}>
                  <span className="capitalize">{s.replace('_', ' ')}</span>
                </Button>
              ))}
            </div>

            {/* Thread */}
            <div className="space-y-3 max-h-[50vh] overflow-y-auto pr-2">
              {(data?.thread || []).map((m, i) => (
                <div key={i} className={`p-3 rounded-lg border ${m.author_role === 'super_admin' ? 'bg-indigo-500/10 border-indigo-400/30 ml-8' : 'bg-white/5 border-white/10 mr-8'}`}>
                  <div className="flex items-center justify-between mb-2 text-xs">
                    <span className="font-semibold">{m.author_email || 'unknown'}</span>
                    <span className="text-slate-500">{m.at ? new Date(m.at).toLocaleString() : ''}</span>
                  </div>
                  <p className="text-sm text-slate-200 whitespace-pre-wrap">{m.body}</p>
                </div>
              ))}
            </div>

            {/* Reply */}
            <div className="space-y-2 pt-2 border-t border-white/5">
              <Label>Your Reply</Label>
              <textarea value={reply} onChange={e => setReply(e.target.value)}
                        placeholder="Type your response..."
                        className="w-full bg-white/5 border border-white/10 rounded-lg p-2 text-sm text-white min-h-[80px]"
                        data-testid="ticket-reply-body" />
              <Button onClick={() => respondMut.mutate()} disabled={!reply || respondMut.isPending}
                      className="bg-indigo-500 hover:bg-indigo-400 text-white" data-testid="ticket-reply-submit">
                <Send className="h-4 w-4 mr-1" /> {respondMut.isPending ? 'Sending...' : 'Send Reply'}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
